/**
 * Support Center FAQ topics, ticket categories, guide sections, and the
 * account-deletion reason list.
 *
 * MOCK/FIXTURE DATA, moved verbatim from the monolith. Copy is product content
 * — do not reword. A backend would serve these from a CMS or a help table.
 */
import { Coins as CoinsIcon, Crown, Music2, Users } from 'lucide-react';

import type { AgreementSection, FaqTopic, GuideSection } from '../types/domain';

export const FAQ_TOPICS: FaqTopic[] = [
  {
    id: 'billing',
    icon: Crown,
    title: 'Billing & plans',
    desc: 'Upgrades, lifetime pricing, refunds',
    questions: [
      {
        q: 'Is the plan really a one-time payment?',
        a: 'The offer card states whether its access is a one-time purchase or yearly. Review that billing label before checkout.',
      },
      {
        q: 'Can I upgrade my plan later?',
        a: "Anytime, from Settings → Manage plan. Upgrading unlocks that product right away — and you keep every song and credit already on your account.",
      },
      {
        q: 'Do you offer refunds?',
        a: "Reach out through a support ticket within your first 14 days with your order details and we'll take care of it.",
      },
      {
        q: "What's included in the Reseller License?",
        a: 'The Reseller License gives you the right to resell the Lyrixsa AI app under your own authority, subject to the buyer-owner agreement. It includes the resale materials and guide needed to present and operate the resale offer. It does not transfer ownership of Lyrixsa, its underlying technology, or its models.',
      },
    ],
  },
  {
    id: 'generating',
    icon: Music2,
    title: 'Generating songs',
    desc: 'Prompts, styles, generation limits',
    questions: [
      {
        q: 'How do I write a good prompt?',
        a: 'Be specific about the musical identity, emotion, story, arrangement, and performance. The example below shows how much detail you can provide. Lyrixsa will interpret the brief creatively, so treat it as direction rather than a guaranteed exact specification.',
      },
      {
        q: 'Can I choose the vocal style?',
        a: 'Yes. Choose male vocal, female vocal, or instrumental only from the Create screen, or set a default under Settings → Preferences. You can also describe the performer in detail: for example, "adult female alto, smoky low register, intimate breath control, restrained runs, clear diction, behind-the-beat verses, and wide layered harmonies only in the final chorus." Other useful details include vocal range, tone, pronunciation, language, rap or singing delivery, vibrato, grit, falsetto, call-and-response, backing vocals, ad-libs, and where the voice should become more intense. Voice Packs provide ready-made descriptions such as Velvet Soul Tenor, Arena Rock Powerhouse, Afrobeats Golden Lead, Crystal Pop Soprano, or Melodic Rap Glow. Select or copy a pack as a starting point, then adapt its direction to your song. Do not request an imitation of a named living artist.',
      },
      {
        q: 'How long does generation take?',
        a: "Most tracks are ready in under a minute. You'll see a live status on the Create screen while it's working.",
      },
      {
        q: "Can I regenerate a song I don't like?",
        a: 'Yes. Adjust the prompt and run it again. Each attempt is a new interpretation, so changing the mood, structure, vocal description, instruments, or unwanted elements can meaningfully change the result.',
      },
    ],
  },
  {
    id: 'credits',
    icon: CoinsIcon,
    title: 'Credits & top-ups',
    desc: 'How credits work, buying more',
    questions: [
      {
        q: 'How do credits work?',
        a: 'Creation actions use the credit amount shown in the app. Unlimited includes an expiring monthly allowance; purchased top-up credits are tracked separately and do not expire.',
      },
      {
        q: 'What happens when I run out?',
        a: "You'll see a prompt to buy more credits right when you try to generate. Credit packs are one-time purchases that are added to your balance and stay there until you use them.",
      },
      {
        q: 'Do unused credits roll over?',
        a: "Purchased top-up credits roll over and do not expire. Unlimited monthly allowance credits expire when the next monthly allowance is issued.",
      },
      {
        q: 'Where can I check my balance?',
        a: "It's a quiet line under the Generate button, or the full breakdown lives in Settings — we keep it off the main dashboard so it doesn't clutter things.",
      },
    ],
  },
  {
    id: 'account',
    icon: Users,
    title: 'Account & login',
    desc: 'Password, email, account access',
    questions: [
      {
        q: 'How do I change my email or password?',
        a: "Head to Settings → your profile section. Changes take effect immediately and we'll send a confirmation email.",
      },
      {
        q: "I can't log in — what should I do?",
        a: 'Try resetting your password from the login screen first. If that doesn\'t work, open a support ticket under "Account access" and we\'ll help sort it out.',
      },
      {
        q: 'Can I delete my account?',
        a: "Yes, from Settings → Delete account. It's permanent, so we'll ask you to confirm and share a quick reason before it goes through.",
      },
      {
        q: 'Can I use Lyrixsa AI on multiple devices?',
        a: 'Yes — log in with the same account anywhere and your library, credits, and plan stay in sync.',
      },
    ],
  },
];

export const TICKET_CATEGORIES = [
  'Billing',
  'Song generation',
  'Account access',
  'Bug report',
  'Other',
];

export const DELETE_REASONS = [
  "I don't use Lyrixsa AI enough",
  'Too expensive',
  'Missing a feature I need',
  'Switching to another tool',
  'Had a bad experience',
  'Other',
];

export const GUIDE_SECTIONS: GuideSection[] = [
  {
    title: 'Getting started',
    body: 'Type a description of the song you want in the Create tab — mood, genre, and story all help. Pick a genre and vocal style chip, then hit Generate. Your first track is usually ready in under a minute.',
  },
  {
    title: 'Writing effective prompts',
    body: 'Specific beats specific: instead of "a happy song," try "an upbeat acoustic song about reuniting with an old friend." Mention tempo, instruments, or a moment in the story for the best results.',
  },
  {
    title: 'Managing credits',
    body: 'Creation actions use credits from your balance. Unlimited monthly allowance credits reset each month, while purchased top-up credits do not expire.',
  },
  {
    title: 'Exporting your song',
    body: 'Open any track in your Library and tap the download icon to save it. You can choose MP3 or WAV as your default export format from Settings → Preferences.',
  },
];

export const AGREEMENT_SECTIONS: AgreementSection[] = [
  {
    title: 'Overview',
    body: 'This Commercial License Agreement ("Agreement") governs the commercial use of music generated through Lyrixsa AI by customers who have purchased the Commercial License & Creator Toolkits.',
  },
  {
    title: 'Definitions',
    body: '"Licensed Track" means any song generated through your Lyrixsa AI account after the Commercial License & Creator Toolkits product is active. "You" or "Licensee" means the account holder.',
  },
  {
    title: 'Rights Granted',
    body: 'Subject to this Agreement, Lyrixsa AI grants you a worldwide, non-exclusive, lifetime license to use Licensed Tracks for commercial purposes as described in the Rights section of your dashboard.',
  },
  {
    title: 'Commercial Usage',
    body: 'You may sell, distribute, monetize, and publicly perform Licensed Tracks, including on streaming platforms, in videos, advertising, client work, podcasts, and courses.',
  },
  {
    title: 'Restrictions',
    body: 'You may not resell or sublicense the Lyrixsa AI generation service itself, misrepresent authorship of the underlying AI model, or use Licensed Tracks for unlawful, defamatory, or hateful content.',
  },
  {
    title: 'Ownership',
    body: 'You own the output of your generations for commercial purposes described herein. Lyrixsa AI retains ownership of its underlying models, software, and technology.',
  },
  {
    title: 'Intellectual Property',
    body: "Lyrixsa AI's trademarks, branding, and platform design remain the property of Lyrixsa AI and are not licensed under this Agreement.",
  },
  {
    title: 'Streaming Platforms',
    body: "Licensed Tracks may be uploaded to Spotify, Apple Music, Amazon Music, YouTube Music, Deezer, and comparable platforms, subject to each platform's own distribution terms.",
  },
  {
    title: 'Client Work',
    body: "You may deliver Licensed Tracks as part of paid work for clients, provided the client's use also remains consistent with this Agreement.",
  },
  {
    title: 'YouTube Monetization',
    body: 'Licensed Tracks may be used in monetized YouTube videos and other ad-supported content without additional royalty payments to Lyrixsa AI.',
  },
  {
    title: 'Termination',
    body: 'This license remains active for as long as your Commercial License & Creator Toolkits purchase remains valid and in good standing. Lyrixsa AI may suspend rights in cases of confirmed abuse or fraud.',
  },
  {
    title: 'Refunds',
    body: "Refund requests for the Commercial License & Creator Toolkits are handled under Lyrixsa AI's standard refund policy, available via a support ticket.",
  },
  {
    title: 'Warranty Disclaimer',
    body: 'Licensed Tracks are provided "as is." Lyrixsa AI makes no warranty that any specific track is free of unintentional similarity to existing works.',
  },
  {
    title: 'Limitation of Liability',
    body: "Lyrixsa AI's liability arising from this Agreement is limited to the amount paid for the Commercial License & Creator Toolkits.",
  },
  {
    title: 'Changes',
    body: 'Lyrixsa AI may update this Agreement from time to time. Continued use of Licensed Tracks after an update constitutes acceptance of the revised terms.',
  },
  {
    title: 'Contact',
    body: 'Questions about this Agreement can be directed to Lyrixsa AI support via the Support Center or a Support Ticket.',
  },
  {
    title: 'Acceptance',
    body: 'By purchasing the Commercial License & Creator Toolkits and generating Licensed Tracks, you accept the terms of this Agreement.',
  },
];
